import React from 'react';
import Input from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import { AppIcon } from '../../../components/icons/AppIcon';
import { formatPhoneWithDDI } from '../../../utils/formatters';
import { cn } from '@/lib/utils';
import RegisterPlanPicker from './RegisterPlanPicker';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import type { PlanId } from '../../../domain/entities/Subscription';

interface InviteDetails {
  organization_name: string;
  email: string;
}

export interface RegisterFormProps {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  plan: PlanId;
  onPlanChange: (plan: PlanId) => void;
  isLoading: boolean;
  error?: string | null;
  successMessage?: string | null;
  inviteToken?: string | null;
  inviteDetails?: InviteDetails | null;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onNavigateToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  name, email, phone, password, confirmPassword, plan, onPlanChange,
  isLoading, error, successMessage, inviteToken, inviteDetails,
  onNameChange, onEmailChange, onPhoneChange, onPasswordChange, onConfirmPasswordChange,
  onSubmit, onNavigateToLogin,
}) => {
  const showPasswordMatch = confirmPassword.length > 0;
  const passwordsMatch = showPasswordMatch && password === confirmPassword;

  return (
  <div className="flex flex-col gap-s6">
    <header className="flex flex-col items-center gap-s2 text-center">
      <h1 id="register-title" className="text-2xl text-default md:text-3xl">
        {inviteDetails ? `Junte-se à ${inviteDetails.organization_name}` : 'Criar nova conta'}
      </h1>
      <p className="text-sm text-secondary">
        {inviteDetails
          ? 'Crie sua conta para acessar a organização e colaborar com a equipe.'
          : 'Crie sua conta Adlitics e comece a acompanhar leads no WhatsApp.'}
      </p>
    </header>

    {inviteDetails && (
      <aside
        className="flex items-start gap-s3 rounded-lg border border-brand-200 bg-primary-subtle px-s4 py-s3 text-left"
        aria-label="Convite verificado"
      >
        <AppIcon name="check-circle" className="mt-0.5 size-4 shrink-0 text-primary" />
        <div>
          <p className="text-label text-primary">Convite verificado</p>
          <p className="mt-s1 text-sm text-secondary">
            Este convite é válido para{' '}
            <span className="font-medium text-default">{inviteDetails.email}</span>.
          </p>
        </div>
      </aside>
    )}

    {successMessage && (
      <div
        role="status"
        className="flex items-start gap-s3 rounded-lg border border-success/30 bg-success-bg px-s4 py-s3 text-left"
      >
        <AppIcon name="check" className="mt-0.5 size-4 shrink-0 text-success-text" />
        <p className="text-sm font-medium text-success-text">{successMessage}</p>
      </div>
    )}

    {error && (
      <div
        role="alert"
        className="flex items-start gap-s3 rounded-lg border border-error/30 bg-danger-bg px-s4 py-s3 text-left"
      >
        <AppIcon name="alert" className="mt-0.5 size-4 shrink-0 text-danger-text" />
        <p className="text-sm font-medium text-danger-text">{error}</p>
      </div>
    )}

    <form onSubmit={onSubmit} className="flex flex-col gap-s4 text-left" aria-busy={isLoading}>
      <Input
        label="Nome completo"
        id="name"
        type="text"
        placeholder="Seu nome completo"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        required
        autoComplete="name"
        inputSize="lg"
        autoFocus
      />

      <Input
        label="E-mail"
        id="email"
        type="email"
        placeholder="voce@email.com"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        required
        disabled={!!inviteDetails}
        autoComplete="email"
        helperText={inviteDetails ? 'Definido pelo convite' : undefined}
        inputSize="lg"
      />
      <Input
        label="Telefone (WhatsApp)"
        id="phone"
        type="tel"
        placeholder="+55 (11) 91234-5678"
        value={phone}
        onChange={(e) => onPhoneChange(formatPhoneWithDDI(e.target.value))}
        required
        autoComplete="tel"
        inputSize="lg"
        className="font-mono"
      />

      <div className="flex flex-col gap-s4">
        <div>
          <Input
            label="Senha"
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            required
            autoComplete="new-password"
            inputSize="lg"
          />
          <PasswordStrengthMeter password={password} />
        </div>
        <div>
          <Input
            label="Confirmar senha"
            id="confirm-password"
            type="password"
            placeholder="Repita a senha"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            required
            autoComplete="new-password"
            inputSize="lg"
          />
          {showPasswordMatch && (
            <p
              className={cn(
                'mt-s2 text-xs font-medium',
                passwordsMatch ? 'text-success-text' : 'text-danger-text',
              )}
              aria-live="polite"
            >
              {passwordsMatch ? 'Senhas coincidem ✓' : 'Senhas não coincidem'}
            </p>
          )}
        </div>
      </div>

      {!inviteToken && <RegisterPlanPicker value={plan} onChange={onPlanChange} />}

      <Button
        type="submit"
        variant="default"
        size="lg"
        disabled={isLoading}
        className="mt-s1 min-h-11 w-full touch-manipulation hover:shadow-brand"
      >
        {isLoading ? <Spinner size="sm" className="mr-s2" /> : null}
        {isLoading
          ? 'Cadastrando...'
          : inviteToken
            ? 'Criar conta e aceitar'
            : 'Criar conta'}
      </Button>
    </form>

    <p className="text-center text-sm text-secondary">
      Já tem uma conta?{' '}
      <button
        type="button"
        onClick={onNavigateToLogin}
        className="font-medium text-primary transition-colors hover:text-primary-dark"
      >
        Entrar na conta
      </button>
    </p>
  </div>
  );
};

export default RegisterForm;
